<template>
  <div class="af">

    <!-- Invalid / expired link -->
    <template v-if="linkInvalid">
      <div class="af-reset-icon">🔗</div>
      <h1 class="af-h1">Link expired</h1>
      <p class="af-sub">This password reset link is invalid or has already been used.</p>
      <RouterLink to="/auth/login" class="af-submit" style="display:block;text-align:center;text-decoration:none;margin-top:8px">
        Back to sign in
      </RouterLink>
    </template>

    <!-- Success -->
    <template v-else-if="success">
      <div class="af-reset-icon">✓</div>
      <h1 class="af-h1">Password updated</h1>
      <p class="af-sub">Your password has been changed. You can now sign in with your new password.</p>
      <RouterLink to="/auth/login" class="af-submit" style="display:block;text-align:center;text-decoration:none;margin-top:8px">
        Sign in →
      </RouterLink>
    </template>

    <!-- Reset form -->
    <template v-else>
      <p class="af-eyebrow">Password Reset</p>
      <h1 class="af-h1">Choose a new password</h1>
      <p v-if="accountEmail" class="af-sub">Resetting password for <strong>{{ accountEmail }}</strong></p>
      <p v-else class="af-sub">Enter your new password below.</p>

      <div v-if="error" class="af-error">{{ error }}</div>

      <form @submit.prevent="handleSubmit" class="af-form">
        <div class="af-field">
          <label class="af-label">New password</label>
          <input
            v-model="newPassword"
            type="password"
            required
            minlength="8"
            placeholder="At least 8 characters"
            class="af-input"
            :disabled="verifying"
          />
        </div>
        <div class="af-field">
          <label class="af-label">Confirm new password</label>
          <input
            v-model="confirmPassword"
            type="password"
            required
            placeholder="Repeat your new password"
            class="af-input"
            :disabled="verifying"
          />
        </div>
        <button type="submit" :disabled="loading || verifying" class="af-submit">
          {{ loading ? 'Updating…' : 'Set new password' }}
        </button>
      </form>
    </template>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth } from '@/composables/useAuth';

const route = useRoute();
const { verifyResetCode, confirmPasswordReset } = useAuth();

const oobCode      = route.query.oobCode || '';
const accountEmail = ref('');
const newPassword  = ref('');
const confirmPassword = ref('');
const loading      = ref(false);
const verifying    = ref(true);
const linkInvalid  = ref(false);
const success      = ref(false);
const error        = ref('');

onMounted(async () => {
  if (!oobCode) {
    linkInvalid.value = true;
    verifying.value = false;
    return;
  }
  try {
    accountEmail.value = await verifyResetCode(oobCode);
  } catch {
    linkInvalid.value = true;
  } finally {
    verifying.value = false;
  }
});

async function handleSubmit() {
  error.value = '';
  if (newPassword.value !== confirmPassword.value) {
    error.value = 'Passwords do not match.';
    return;
  }
  if (newPassword.value.length < 8) {
    error.value = 'Password must be at least 8 characters.';
    return;
  }
  loading.value = true;
  try {
    await confirmPasswordReset(oobCode, newPassword.value);
    success.value = true;
  } catch (e) {
    error.value = 'Could not update your password. The link may have expired — request a new one.';
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.af { font-family: 'Jost', sans-serif; }

.af-reset-icon {
  font-size: 40px;
  margin-bottom: 16px;
  text-align: center;
}

.af-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #B8902A;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.af-eyebrow::before {
  content: '';
  width: 24px;
  height: 1px;
  background: #B8902A;
  flex-shrink: 0;
}

.af-h1 {
  font-family: 'Cormorant Garamond', serif;
  font-size: 34px;
  font-weight: 600;
  color: #0D1628;
  line-height: 1.15;
  margin-bottom: 6px;
  letter-spacing: -0.01em;
}

.af-sub {
  font-size: 14px;
  color: #888;
  font-weight: 300;
  margin-bottom: 28px;
  line-height: 1.6;
}

.af-error {
  margin-bottom: 18px;
  background: #fdf2f2;
  border: 1px solid #f5c6c6;
  color: #b91c1c;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 400;
}

.af-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.af-field { display: flex; flex-direction: column; gap: 6px; }

.af-label {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.af-input {
  width: 100%;
  border: 1.5px solid #e0dbd0;
  border-radius: 4px;
  padding: 11px 14px;
  font-size: 14px;
  font-family: 'Jost', sans-serif;
  font-weight: 400;
  color: #0D1628;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;
}
.af-input:focus { border-color: #B8902A; }
.af-input::placeholder { color: #bbb; }
.af-input:disabled { opacity: 0.55; }

.af-submit {
  width: 100%;
  background: #B8902A;
  color: #0D1628;
  border: none;
  border-radius: 4px;
  padding: 13px;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Jost', sans-serif;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 4px;
}
.af-submit:hover:not(:disabled) {
  background: #d4a843;
  transform: translateY(-1px);
}
.af-submit:disabled { opacity: 0.55; cursor: not-allowed; }
</style>
